/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-01 15:08:58
 * @ Modified by: David Helmick
 * @ Modified time: 2025-09-24 11:07:02
 * @ Description: Dialog for the editing of property reports
 */

import React, { useState, useEffect, useRef } from 'react'
// Centralized lodash imports - much cleaner!
import {
  truncate,
  isNull,
  map,
  slice,
  compact,
  indexOf,
  isEqual,
  find,
  matchesProperty,
  isEmpty,
} from '@/utils/lodash'
import { useMutation, useQuery } from '@apollo/client/react'
import Avatar from '@mui/material/Avatar'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
//import { Link, LinkIcon, GetAppIcon } from '@mui/icons-material/'
import {
  Grid,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { styled } from '@mui/material/styles'
import GetAppIcon from '@mui/icons-material/GetApp'
import Dropzone from 'react-dropzone'
import loadImage from '@/utils/loadImage'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { Check } from '@mui/icons-material'
import withSession from '@/housefolios-components/Session/withSession'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GET_TEAM } from '@/housefolios-components/Team/queries'
import { IMAGE_UPLOAD } from '@/housefolios-components/TitleButtons/AddPropertyButton/mutations'
import { GET_PROPERTY } from '../queries'
import { UPDATE_PROPERTY, UPDATE_PROPERTIES } from '../mutations'
import ProformaLogoUploader from './proformaLogoUploader'
import { getStrategyName } from '@/utils/stratagies'
import { enqueueSnackbar } from 'notistack'
import PropertyPDFRefactored from './PropertyPDFRefactored'
import { handleImageUrl } from './utils/pdfUtils'
import { proformaTypeLabel } from './proformaTypeLabel'

const MAXSIZE = 100000000 //bytes
const FILETYPES = ['image/*']

function ProformaPickThumb({
  image,
  preview,
  overlay,
  onClick,
  boxWidth = 160,
  boxHeight = 110,
  imgWidth = 150,
  imgHeight = 100,
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: `${boxWidth}px`,
        height: `${boxHeight}px`,
        verticalAlign: 'top',
        overflow: 'hidden',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          ...{ backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" },
          width: `${boxWidth}px`,
          height: `${boxHeight}px`,
          backgroundImage: `url(${preview })`,
          backgroundSize: `${imgWidth}px ${imgHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={image}
          alt=""
          loading="lazy"
          sx={{
            width: `${imgWidth}px`,
            height: `${imgHeight}px`,
            m: '5px',
            display: 'block',
            objectFit: 'cover',
          }}
        />
      </Box>
      {overlay != null && (
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            right: 5,
            bottom: 5,
            left: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 50,
            lineHeight: 1,
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.4)',
          }}
        >
          {overlay}
        </Box>
      )}
    </Box>
  )
}

const INITIAL_STATE = {
  activeTab: 0,
  // steps: ['Pictures', 'Proforma Notes' /*'Pages (Coming Soon)'*/],
  circle: false,
  show: false,
  backDrop: false,
  legalDisclaimer: false,
}

const EditProforma = (props) => {
  const [show, setShow] = useState(false)
  const [showBulkProforma, setShowBulkProforma] = useState(false)
  const [proformaDescription, setProformaDescription] = useState(
    props.proformaDescription ||
      truncate(props.description, { length: 300  }) ||
      '',
  )
  const [proformaImages, setProformaImages] = useState(
    !isEmpty(props.proformaImages)
      ? props.proformaImages
      : map(slice(props.images, 0, 4), (image) => image.url),
  )
  const [proformaImagePage, setProformaImagePage] = useState(
    props.proformaImagePage,
  )
  const [proformaLogo, setProformaLogo] = useState(props.proformaLogo)
  const [proformaFloorPlan, setProformaFloorPlan] = useState(
    props.proformaFloorPlan,
  )
  const [proformaRehabCost, setProformaRehabCost] = useState(
    !isNull(props.proformaRehabCost) ? props.proformaRehabCost : true,
  )
  const [proformaHideAddress, setProformaHideAddress] = useState(
    !isNull(props.proformaHideAddress)
      ? props.proformaHideAddress
      : true,
  )
  const [proformaContact, setProformaContact] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [steps, setSteps] = useState(
    props.bulk
      ? ['Logo', 'Proforma Notes']
      : props?.session?.me?.activeOrg?.southernImpressions &&
          props.type === 'marketing'
        ? ['Pictures', 'Logo', 'Floor Plan', 'Proforma Notes']
        : [
            'Pictures',
            'Logo',
            'Proforma Notes' /*'Pages (Coming Soon)'*/,
          ],
  )
  const [file, setFile] = useState(props?.property?.proformaFile)
  const [tab, setTab] = useState(
    props?.session?.me?.activeOrg?.member ? 1 : 0,
  )
  const [circle, setCircle] = useState(false)
  const [backDrop, setBackDrop] = useState(false)
  const [type, setType] = useState(props.type || 'buyHold')
  const [legalDisclaimer, setLegalDisclaimer] = useState(false)

  const { images, propertyId, property } = props

  const [imageUpload] = useMutation(IMAGE_UPLOAD)

  const [updatePropertyMutation] = useMutation(
    props.bulk ? UPDATE_PROPERTIES : UPDATE_PROPERTY,
    {
      refetchQueries: props.bulk
        ? []
        : [{ query: GET_PROPERTY, variables: { id: propertyId } }],
    },
  )

  const updateProperty = () =>
    updatePropertyMutation({
      variables: props.bulk
        ? {
            ids: props.propertyIds,
            propertyInput: {
              proformaLogo,
              proformaContact,
            },
          }
        : {
            id: propertyId,
            propertyInput: {
              proformaDescription,
              proformaImages,
              proformaImagePage,
              proformaLogo,
              proformaFloorPlan,
              proformaRehabCost,
              proformaHideAddress,
              proformaContact,
            },
          },
    })

  const {
    loading: teamLoading,
    error: teamError,
    data: teamData,
  } = useQuery(GET_TEAM)

  const teamMembers = teamData?.teamMembers || []
  const orgCreator = find(
    teamMembers,
    matchesProperty('_id', props.session.me.activeOrg.createdBy._id),
  )

  useEffect(() => {
    if (isEmpty(proformaContact) && orgCreator?._id) {
      setProformaContact(orgCreator._id)
    }
  }, [proformaContact, orgCreator?._id])

  const prevPropsRef = useRef(null)
  useEffect(() => {
    const prevProps = prevPropsRef.current
    if (prevProps && props.type !== prevProps.type) {
      setType(props.type || 'buyHold')
    }
    prevPropsRef.current = props
  }, [props.type])

  // toggleModal = () => {
  //     const { show } = state
  //     setState({ show: !show  })
  // }
  const toggleModal = () => {
    if (props.bulk) {
      setShow(true)
      setProformaDescription('')
      setProformaLogo(props.proformaLogo)
      setProformaContact('')
      setActiveTab(0)
    } else {
      setShow(!show)
      setProformaDescription(
        props.proformaDescription ||
          truncate(props.description, { length: 300  }) ||
          '',
      )
      setProformaImages(
        !isEmpty(props.proformaImages)
          ? props.proformaImages
          : map(slice(props.images, 0, 4), (image) => image.url),
      )
      setProformaImagePage(props.proformaImagePage)
      setProformaLogo(props.proformaLogo)
      setProformaFloorPlan(props.proformaFloorPlan)
      setProformaRehabCost(
        !isNull(props.proformaRehabCost)
          ? props.proformaRehabCost
          : true,
      )
      setProformaHideAddress(
        !isNull(props.proformaHideAddress)
          ? props.proformaHideAddress
          : true,
      )
    }
  }

  const handleCheck = (event) => {
    const { name = '' } = event.target

    if (name === 'proformaRehabCost') {
      setProformaRehabCost(!proformaRehabCost)
    }
    if (name === 'hideAddress') {
      setProformaHideAddress(!proformaHideAddress)
    }
    if (name === 'legalDisclaimer') {
      setLegalDisclaimer(!legalDisclaimer)
    }
    if (name === 'proformaImagePage') {
      setProformaImagePage(!proformaImagePage)
    }
  }

  const readMultiFiles = (files) => {
    return Promise.all(
      map(files, (file) => {
        return new Promise((resolve, reject) => {
          loadImage(
            file,
            (img) => {
              resolve(img.toDataURL(file.type))
            },
            {
              orientation: true,
              canvas: true,
            },
          )
        })
      }),
    )
  }

  const onDrop = (files, rejectedFiles) => {
    //const { addImages } = props
    if (files.length <= 0) {
      // Handle error - could add error state if needed
      console.error('upload a valid file')
    } else {
      setCircle(true)
      readMultiFiles(files).then(async (results) => {
        for (let i = 0; i < results.length; i++) {
          results[i] = await handleImageUrl(
            results[i],
            props.closeSnackbar,
          )
        }
        let images = compact(results)
        imageUpload({
          variables: {
            documentId: propertyId,
            imageUrls: images,
            collection: 'property',
          },
          refetchQueries: [
            {
              query: GET_PROPERTY,
              variables: { id: propertyId },
            },
          ],
        }).then(({ data  }) => {
          setCircle(false)
        })
      })
    }
  }

  const clickImage = (image) => {
    const check = indexOf(proformaImages, image)
    if (check === -1 && proformaImages.length < 4) {
      setProformaImages([...proformaImages, image])
    } else if (check !== -1) {
      setProformaImages(
        proformaImages.filter((_, index) => index !== check),
      )
    }
  }

  const clickImageLogo = (image) => {
    if (!isEqual(proformaLogo, image)) {
      setProformaLogo(image)
    } else {
      setProformaLogo(null)
    }
  }

  const clickImageFloorPlan = (image) => {
    if (!isEqual(proformaFloorPlan, image)) {
      setProformaFloorPlan(image)
    } else {
      setProformaFloorPlan(null)
    }
  }

  const handleBackDrop = () => {
    setBackDrop(true)
    // updateProperty().then(async ({ data  }) => {
    //   //handleClick()
    // })
  }

  const handleClick = () => {
    setShow(false)
    setActiveTab(0)
    setCircle(false)
    setBackDrop(false)
    setLegalDisclaimer(false)
    if (props.bulk && props.onClose) {
      props.onClose()
    }
    if (props.bulk && props.setSelected) {
      props.setSelected()
    }
  }

  const CustomConnector = styled(StepConnector)({
    '&.MuiStepConnector-alternativeLabel': {
      top: 22,
    },
    '& .MuiStepConnector-line': {
      height: 3,
      border: 0,
      backgroundColor: '#eaeaf0',
      borderRadius: 1,
    },
    '&.Mui-active .MuiStepConnector-line': {
      backgroundImage:
        'linear-gradient( 95deg, #56ccf2 0%, #2f80ed 100%)',
    },
    '&.Mui-completed .MuiStepConnector-line': {
      backgroundImage:
        'linear-gradient( 95deg, #56ccf2 0%, #2f80ed 100%)',
    },
  })

  const CustomStepIcon = (props) => {
    const { active, completed } = props

    const icons = {
      1: <i className={'fas fa-arrow-up'} />,
      2: <i className={'fas fa-columns'} />,
      3: <i className={'fas fa-flag'} />,
    }

    return (
      <Box
        sx={{
          backgroundColor: '#ccc',
          zIndex: 1,
          color: '#fff',
          width: 50,
          height: 50,
          display: 'flex',
          borderRadius: '50%',
          justifyContent: 'center',
          alignItems: 'center',
          ...((active || completed) && {
            backgroundImage:
              'linear-gradient( 136deg, #56ccf2 0%, #2f80ed 100%)',
          }),
          ...(active && {
            boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
          }),
        }}
      >
        {icons[String(props.icon)]}
      </Box>
    )
  }

  const onChange = (event) => {
    const { /*name = '',*/ value = '' /*type = ''*/ } = event.target
    setProformaDescription(value)
  }

  const onChangeContact = (event) => {
    const { name = '', value = '' } = event.target
    if (name === 'type') {
      setType(value)
      if (
        !props.bulk &&
        props?.session?.me?.activeOrg?.southernImpressions
      ) {
        if (value === 'marketing') {
          setSteps([
            'Pictures',
            'Logo',
            'Floor Plan',
            'Proforma Notes',
          ])
          setActiveTab(activeTab < 4 ? activeTab : 3)
        } else {
          setSteps(['Pictures', 'Logo', 'Proforma Notes'])
          setActiveTab(activeTab < 3 ? activeTab : 2)
        }
      }
    }
    if (name === 'proformaContact') {
      setProformaContact(value)
    }
  }

  const handleChange = (event, newValue) => {
    setTab(newValue)
  }

  const uploadFile = (newFile) => {
    setFile(newFile)
  }

  const deleteFile = () => {
    setFile({})
  }

  const saveUpload = () => {
    setCircle(true)
    updateProperty().then(() => {
      handleClick()
    })
  }

  const { proformaLogos = [] } = props?.session?.me?.activeOrg || {}

  const pdfProperty = props.bulk
    ? {}
    : {
        _id: property._id,
        address: property.address,
        city: property.city,
        state: property.state,
        zip: property.zip,
        beds: property.beds,
        baths: property.baths,
        sqft: property.sqft,
        year: property.year,
        proformaDescription: property.proformaDescription,
        proformaImages: property.proformaImages,
        proformaImagePage: property.proformaImagePage,
        proformaLogo: property.proformaLogo,
        proformaFloorPlan: property.proformaFloorPlan,
        proformaRehabCost: property.proformaRehabCost,
        proformaHideAddress: property.proformaHideAddress,
        proformaContact: property.proformaContact,
        portfolio: property.portfolio,
        assumptions: property.assumptions,
        acquisition: property.acquisition,
      }

  const uploadPics =
    undefined !== images && images.length <= 0 ? true : false
  const disabled =
    proformaDescription || props.bulk
      ? (proformaDescription.length > 300 && !props.bulk) ||
        !legalDisclaimer ||
        !proformaContact
        ? true
        : false
      : true

  let imageCheck = map(proformaImages, (image) => {
    for (let i = 0; i < images.length; i++)
      if (images[i].url === image) return image
  })
  imageCheck = compact(imageCheck)
  if (!isEqual(proformaImages, imageCheck)) {
    setProformaImages(imageCheck)
  }
  const continueDisabled =
    proformaImages.length > 0 || props.bulk ? false : true

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {!props.bulk && (
        <ListItemButton onClick={toggleModal} sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          cursor: "pointer",
          columnGap: "8px",
          "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "8px",
            minWidth: 0,
            flex: "1 1 auto",
            justifyContent: "flex-start",
            "& > .MuiBox-root:first-of-type": {
              flex: "0 0 auto",
              width: 16,
              minWidth: 16,
              marginRight: 0,
            },
          }}>
            <Box sx={{
              width: 16,
              minWidth: 16,
              flexShrink: 0,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 10,
              fontSize: "1.15rem",
              "& > svg": { width: "1em", height: "1em" },
              ".Mui-selected > &": { opacity: "1 !important" },
            }}>
              <FontAwesomeIcon icon={['far', 'file-lines']} />
            </Box>
            <span>Edit Proforma</span>
          </Box>
        </ListItemButton>
      )}

      <Dialog
        open={
          props.bulk
            ? props.show !== undefined
              ? props.show
              : true
            : show
        }
        onClose={props.bulk ? props.onClose : toggleModal}
        // dialogclassname="modal-proforma"
        fullWidth={true}
        maxWidth="md"
        onKeyDown={(event) => event.stopPropagation()}
      >
        {/* <Tabs value={tab} onChange={handleChange}>
            <Tab label='Create Proforma' disabled={props?.session?.me?.activeOrg?.member} />
            <Tab label='Upload Proforma' />
          </Tabs> */}
        <DialogTitle sx={{ maxHeight: 250 }}>
          {props.bulk ? (
            'Bulk Edit Proforma'
          ) : type === 'buyHold' ? (
            <>
              {getStrategyName(
                'Buy & Hold',
                props?.session?.me?.activeOrg?.member,
              )}{' '}
              Proforma
            </>
          ) : type === 'fixFlip' ? (
            <>
              {getStrategyName(
                'Fix & Flip',
                props?.session?.me?.activeOrg?.member,
              )}{' '}
              Proforma
            </>
          ) : type === 'shortTermRental' ? (
            <>
              {getStrategyName(
                'Short Term Rental',
                props?.session?.me?.activeOrg?.member,
              )}{' '}
              Proforma
            </>
          ) : type === 'marketing' ? (
            <>
              {getStrategyName(
                'Turnkey: End Buyer',
                props?.session?.me?.activeOrg?.member,
              )}{' '}
              Proforma
            </>
          ) : (
            getStrategyName(
              'Wholesale',
              props?.session?.me?.activeOrg?.member,
            )
          )}
          <Stepper
            activeStep={activeTab}
            color="primary"
            style={{ borderTop: 'transparent', padding: 24 }}
            //style={{ width: '75%' }}
          >
            {map(steps, (label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>
        <DialogContent>
          <DialogContent onClick={(event) => event.stopPropagation()}>
            <Grid container>
              {!props.bulk && (
                <Grid size={{ sm: 12 }}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth={true}
                  >
                    <InputLabel>{'Proforma type:'}</InputLabel>
                    <Select
                      name="type"
                      value={type}
                      onChange={(event) => onChangeContact(event)}
                      label="Proforma type:"
                      renderValue={(value) =>
                        proformaTypeLabel(value, props.session)
                      }
                      inputProps={{
                        name: 'type',
                      }}
                    >
                      <MenuItem key="buyhold" value={'buyHold'}>
                        <ListItemText
                          primary={getStrategyName(
                            'Buy & Hold',
                            props?.session?.me?.activeOrg?.member,
                          )}
                        />
                      </MenuItem>
                      <MenuItem key="fixFlip" value={'fixFlip'}>
                        <ListItemText
                          primary={getStrategyName(
                            'Fix & Flip',
                            props?.session?.me?.activeOrg?.member,
                          )}
                        />
                      </MenuItem>
                      <MenuItem key="marketing" value={'marketing'}>
                        <ListItemText
                          primary={
                            props.session.me.activeOrg
                              .southernImpressions
                              ? getStrategyName(
                                  'Build to Rent',
                                  props?.session?.me?.activeOrg
                                    ?.member,
                                )
                              : 'End Buyer'
                          }
                        />
                      </MenuItem>
                      <MenuItem
                        key="shortTermRental"
                        value={'shortTermRental'}
                      >
                        <ListItemText
                          primary={getStrategyName(
                            'Short Term Rental',
                            props?.session?.me?.activeOrg?.member,
                          )}
                        />
                      </MenuItem>
                      {props?.session?.me?.activeOrg?.member !==
                        'astroflip' && (
                        <MenuItem key="wholesale" value={'wholesale'}>
                          <ListItemText
                            primary={getStrategyName(
                              'Wholesale',
                              props?.session?.me?.activeOrg?.member,
                            )}
                          />
                        </MenuItem>
                      )}
                      {props?.session?.me?.activeOrg?.member ===
                        'astroflip' && (
                        <MenuItem key="astroflip" value={'astroflip'}>
                          <ListItemText
                            primary={getStrategyName(
                              'Astroflip',
                              props?.session?.me?.activeOrg?.member,
                            )}
                          />
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid size={12}>
                {steps[activeTab] === 'Pictures' &&
                  (!uploadPics ? (
                    <Grid container sx={{ alignItems: 'flex-start' }}>
                      <Grid size={{ sm: 12 }}>
                        <h3>Select Proforma Display Images</h3>
                      </Grid>
                      <Grid size={{ sm: 12, md: 7 }}>
                        <Card
                          style={{
                            width: '100%',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            backgroundColor: 'white',
                            height: 450,
                            overflowY: 'scroll',
                            boxShadow: 'none',
                          }}
                        >
                          <CardContent>
                            {map(images, (url) => {
                              const image = url.url
                              const check = indexOf(
                                proformaImages,
                                image,
                              )
                              return (
                                <ProformaPickThumb
                                  key={image}
                                  image={image}
                                  preview={url.url_small}
                                  overlay={
                                    check === -1 ? null : check + 1
                                  }
                                  onClick={() => clickImage(image)}
                                />
                              )
                            })}
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid size={{ sm: 12, md: 5 }}>
                        <img
                          src="/images/proforma_image.png"
                          alt=""
                          style={{
                            boxShadow: '0 4px 10px 0 #bbbbbb',
                            width: '100%',
                          }}
                        />
                        <FormControlLabel
                          key="proformaImagePage"
                          control={
                            <Checkbox
                              name="proformaImagePage"
                              required={true}
                              value={proformaImagePage}
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize="large" />
                              }
                              checkedIcon={
                                <CheckBoxIcon
                                  fontSize="large"
                                  style={{ color: '#2296F3' }}
                                />
                              }
                              checked={proformaImagePage}
                              onChange={(event) => handleCheck(event)}
                              color="primary"
                              style={{ marginTop: 'auto' }}
                            />
                          }
                          label="Include all property images on the end of the report"
                        ></FormControlLabel>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container>
                      <Grid size={{ sm: 12 }}>
                        <h3>Select Proforma Display Images</h3>
                      </Grid>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <Dropzone
                          onDrop={onDrop}
                          accept={{ 'image/*': [] }}
                          maxSize={MAXSIZE}
                          multiple={true}
                        >
                          {({ getRootProps, getInputProps  }) => (
                            <section style={{ padding: 0 }}>
                              <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Card
                                  style={{
                                    boxShadow: 'none',
                                  }}
                                >
                                  <CardContent>
                                    <div>
                                      <Avatar
                                        sizes="75px"
                                        style={{
                                          fontSize: '75px',
                                          width: '160px',
                                          height: '160px',
                                          margin: '10px',
                                          marginLeft: '120px',
                                          backgroundColor: '#e9e9e9',
                                          alignSelf: 'center',
                                        }}
                                      >
                                        <GetAppIcon
                                          style={{
                                            color: '#949494',
                                          }}
                                          fontSize="inherit"
                                        />
                                      </Avatar>
                                      <div
                                        style={{
                                          fontSize: '22px',
                                          color: 'grey',
                                        }}
                                      >
                                        Drag and drop a file you want
                                        to upload
                                      </div>
                                    </div>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      style={{
                                        margin: 10,
                                        marginLeft: 140,
                                      }}
                                    >
                                      Select Files
                                    </Button>
                                    {circle && (
                                      <div>
                                        {/* <Grid
                                              container
                                              justifyContent="center"
                                            >
                                              <Grid> */}
                                        <CircularProgress
                                          style={{
                                            marginLeft: '44%',
                                            marginTop: 10,
                                            marginBottom: 10,
                                          }}
                                        />
                                        {/* </Grid>
                                            </Grid> */}
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>
                            </section>
                          )}
                        </Dropzone>
                      </Grid>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <img
                          src="/images/proforma_image.png"
                          alt=""
                          style={{
                            border: '2px',
                            borderColor: 'grey',
                            borderStyle: 'solid',
                          }}
                        />
                        <FormControlLabel
                          key="proformaImagePage"
                          control={
                            <Checkbox
                              name="proformaImagePage"
                              value={proformaImagePage}
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize="large" />
                              }
                              checkedIcon={
                                <CheckBoxIcon
                                  fontSize="large"
                                  style={{ color: '#2296F3' }}
                                />
                              }
                              checked={proformaImagePage}
                              onChange={(event) => handleCheck(event)}
                              color="primary"
                              style={{ marginTop: 'auto' }}
                            />
                          }
                          label="Include all property images on the end of the report"
                        ></FormControlLabel>
                      </Grid>
                    </Grid>
                  ))}
                {steps[activeTab] === 'Logo' && (
                  <Grid container>
                    <Grid size={{ sm: 12 }}>
                      <h3>Select Logo for Proforma</h3>
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <p>
                        Defaults to Organization Logo if none are
                        selected
                      </p>
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <ProformaLogoUploader />
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <Card
                        style={{
                          width: '100%',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          backgroundColor: 'white',
                          height: 450,
                          overflowY: 'scroll',
                          boxShadow: 'none',
                        }}
                      >
                        <CardContent>
                          {map(proformaLogos, (url) => {
                            const image = url.url
                            const check = proformaLogo === image
                            return (
                              <ProformaPickThumb
                                key={image}
                                image={image}
                                preview={url.url_small}
                                boxWidth={260}
                                imgWidth={250}
                                overlay={
                                  check ? (
                                    <Check
                                      style={{
                                        width: 50,
                                        height: 50,
                                      }}
                                    />
                                  ) : null
                                }
                                onClick={() => clickImageLogo(image)}
                              />
                            )
                          })}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                {steps[activeTab] === 'Floor Plan' && (
                  <Grid container>
                    <Grid size={{ sm: 12 }}>
                      <h3>Select Floor Plan Image</h3>
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <Card
                        style={{
                          width: '100%',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          backgroundColor: 'white',
                          height: 450,
                          overflowY: 'scroll',
                          boxShadow: 'none',
                        }}
                      >
                        <CardContent>
                          {map(images, (url) => {
                            const image = url.url
                            const check = proformaFloorPlan === image
                            return (
                              <ProformaPickThumb
                                key={image}
                                image={image}
                                preview={url.url_small}
                                overlay={check ? 1 : null}
                                onClick={() =>
                                  clickImageFloorPlan(image)
                                }
                              />
                            )
                          })}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                {steps[activeTab] === 'Proforma Notes' && (
                  <ValidatorForm
                    style={{
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Grid container direction="column">
                      <Grid>
                        <h3>Proforma Notes</h3>
                      </Grid>
                      <Grid size={{ sm: 12 }}>
                        {!props.bulk && (
                          <TextValidator
                            key="proformaDescriptionKey"
                            name="proformaDescription"
                            label="Description*"
                            fullWidth={true}
                            autoFocus
                            value={proformaDescription}
                            multiline
                            rows="12"
                            onChange={(event) => onChange(event)}
                            onBlur={(event) => onChange(event)}
                            validators={[
                            'required',
                            'maxStringLength: 300',
                            ]}
                            errorMessages={[
                            'This field is required',
                            'You cannot exceed 300 characters',
                            ]}
                            margin="dense"
                            variant="outlined"
                            helperText={`${proformaDescription.length}/300 — Long text or too many blank lines may be shortened or shown smaller so it fits on the one-page proforma. Use blank lines and - or * for bullets.`}
                            />
                        )}
                        <Grid size={{ sm: 12 }}>
                          {teamLoading ? (
                            <LoadingSkeleton variant="inline" width={180} height={32} />
                          ) : teamError ? (
                            <div>Error! {teamError.message}</div>
                          ) : (
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth={true}
                            >
                              <InputLabel>
                                {'Contact Info on Proforma:'}
                              </InputLabel>
                              <Select
                                name="proformaContact"
                                value={proformaContact}
                                onChange={(event) =>
                                onChangeContact(event)
                                }
                                label="Contact Info on Proforma:"
                                renderValue={(value) => {
                                  const member = find(
                                    teamMembers,
                                    (item) => item._id === value,
                                  )
                                  return member
                                    ? `${member.profile.firstname} ${member.profile.lastname}`
                                    : ''
                                }}
                                inputProps={{
                                  name: 'proformaContact',
                                }}
                              >
                                {map(teamMembers, (member) => (
                                  <MenuItem value={member._id}>
                                    <ListItemText
                                      primary={`${member.profile.firstname} ${member.profile.lastname}`}
                                      secondary={`${member.email} | ${member.phone}`}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        <Grid size={{ sm: 12 }}>
                          {/* <FormControlLabel
                            <h3>Proforma Notes</h3>
                          </Grid>
                          <Grid size={{sm: 7}}>
                            <TextValidator
                              key="proformaDescriptionKey"
                              name="proformaDescription"
                              label="Description*"
                              fullWidth={true}
                              value={description}
                              multiline
                              rows="12"
                              onChange={event => onChange(event)}
                              onBlur={event => onChange(event)}
                              validators={['required', 'maxStringLength: 300']}
                              errorMessages={['This field is required', 'You cannot exceed 300 characters']}
                              margin="dense"
                              variant="outlined"
                            />
                            <Grid size={{sm: 12}}
                              //style={{ marginTop: 200 }}
                            >
                              {/* <FormControlLabel
                                key="proformaRehabCostKey"
                                control={
                                  <Checkbox
                                    name="proformaRehabCost"
                                    type="checkbox"
                                    value={proformaRehabCost}
                                    checked={
                                      proformaRehabCost
                                    }
                                    onChange={event =>
                                      handleCheck(event)
                                    }
                                    color="primary"
                                    style={{ marginTop: 'auto' }}
                                  />
                                }
                                label="Include Estimated Rehab Cost in Proforma"
                              ></FormControlLabel> */}
                          {type === 'marketing' && (
                            <FormControlLabel
                              key="hideAddressKey"
                              control={
                                <Checkbox
                                  name="hideAddress"
                                  value={proformaHideAddress}
                                  icon={
                                    <CheckBoxOutlineBlankIcon fontSize="large" />
                                  }
                                  checkedIcon={
                                    <CheckBoxIcon
                                      fontSize="large"
                                      style={{
                                        color: '#2296F3',
                                      }}
                                    />
                                  }
                                  checked={proformaHideAddress}
                                  onChange={(event) =>
                                    handleCheck(event)
                                  }
                                  color="primary"
                                  style={{ marginTop: 'auto' }}
                                />
                              }
                              label="Hide Address in Turnkey: End Buyer Proforma"
                            ></FormControlLabel>
                          )}
                        </Grid>
                        <Grid>
                          <FormControlLabel
                            key="legalDisclaimer"
                            style={{ margin: 0 }}
                            control={
                              <Checkbox
                                name="legalDisclaimer"
                                required={true}
                                value={legalDisclaimer}
                                icon={
                                  <CheckBoxOutlineBlankIcon fontSize="large" />
                                }
                                checkedIcon={
                                  <CheckBoxIcon
                                    fontSize="large"
                                    style={{ color: '#2296F3' }}
                                  />
                                }
                                checked={legalDisclaimer}
                                onChange={(event) =>
                                  handleCheck(event)
                                }
                                color="primary"
                                style={{ marginTop: 'auto' }}
                              />
                            }
                            label="By creating this proforma, I represent and warrant that I am the creator and owner of the photos, or have the necessary licenses, rights, consents, and permissions to use the selected photos for marketing purposes."
                          ></FormControlLabel>
                        </Grid>
                      </Grid>
                    </Grid>
                    {backDrop && (
                      <Backdrop
                        open={backDrop}
                        style={{
                          color: '#fff',
                          zIndex: 100000,
                        }}
                      >
                        <CircularProgress color="inherit" />
                      </Backdrop>
                    )}
                  </ValidatorForm>
                )}
              </Grid>
            </Grid>
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <div>
            {activeTab !== 0 && activeTab !== steps.length - 1 && (
              <>
                <Button
                  onClick={(event) => {
                  setActiveTab(activeTab - 1)
                  event.stopPropagation()
                  }}
                  sx={{ mx: '0.5rem' }}
                  >
                  Back
                </Button>
              </>
            )}
            {activeTab !== steps.length - 1 && (
              <>
                <Button
                  onClick={(event) => {
                  setActiveTab(activeTab + 1)
                  event.stopPropagation()
                  }}
                  variant="contained"
                  color="primary"
                  disabled={continueDisabled}
                  sx={{ mx: '0.5rem' }}
                  >
                  Continue
                </Button>
              </>
            )}
            {activeTab === steps.length - 1 && (
              <>
                {props.bulk ? (
                  <>
                    <Button
                      onClick={(event) => {
                      setActiveTab(activeTab - 1)
                      event.stopPropagation()
                      }}
                      sx={{ mx: '0.5rem' }}
                      >
                      Back
                    </Button>
                    <Button
                      onClick={(event) => {
                      handleBackDrop()
                      updateProperty().then(async ({ data  }) => {
                      handleClick()
                      })
                      event.stopPropagation()
                      }}
                      variant="contained"
                      color="primary"
                      disabled={disabled}
                      sx={{ mx: '0.5rem' }}
                      >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <PropertyPDFRefactored
                    property={pdfProperty}
                    propertyId={propertyId}
                    editProforma={true}
                    type={type || 'marketing'}
                    handleBackDrop={handleBackDrop}
                    updateProperty={updateProperty}
                    handleClick={handleClick}
                    handleBackClick={() =>
                      setActiveTab(activeTab - 1)
                    }
                    disabled={disabled}
                  />
                )}
              </>
            )}
          </div>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default withSession(EditProforma)
